// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography, {
    TitleTypographyProps,
    TitleTypographyStyles,
} from "@adl/App/Components/Common/TitleTypography";
import { color, mergeWithCss, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Accordion from "@insite/mobius/Accordion";
import {
    AccordionSectionPanel,
    AccordionSectionPresentationProps,
    ManagedAccordionSection,
} from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    title?: React.ReactNode;
    isAccordion?: boolean;
    className?: string;
    extendedStyles?: SectionWrapperStyles;
    withBorder?: boolean;
}

type Props = OwnProps & Omit<TitleTypographyProps, "extendedStyles">;

export interface SectionaccordionIconStyles {
    managerAccordionSection?: AccordionSectionPresentationProps;
    bodyGridContainer?: GridContainerProps;
}

export interface SectionWrapperStyles {
    title?: TitleTypographyStyles;
    accrodionTitle?: TitleTypographyStyles;
    wrapper?: InjectableCss;
    accordionStyles?: SectionaccordionIconStyles;
}

const accordionIconStyle = css`
    &:after {
        font-size: 14px;
        font-weight: bold;
        letter-spacing: 0;
        line-height: 19px;
        text-align: center;
        margin-left: 8px;
        white-space: nowrap;
    }
    ${xScreen("lte", "xs")} {
        svg {
            display: none;
        }
    }
    &:hover {
        &:after {
            color: ${color("common.ui1")};
        }
    }
`;

const sectionWrapperStyles: SectionWrapperStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${({ withBorder }: any) => {
                if (!withBorder) {
                    return "";
                }
                return css`
                    padding-top: 16px;
                    border-top: 1px solid ${color("common.border")};
                `;
            }}
        `,
    },
    accordionStyles: {
        managerAccordionSection: {
            mergeCss: true,
            toggleIconProps: {
                mergeCss: true,
                css: css`
                    width: auto;
                    ${getProp("theme.accordion.sectionDefaultProps.toggleIconProps.css")};
                    &:after {
                        content: "Expand";
                    }
                    ${accordionIconStyle}
                `,
            },
            collapseIconProps: {
                mergeCss: true,
                css: css`
                    width: auto;
                    ${getProp("theme.accordion.sectionDefaultProps.toggleIconProps.css")};
                    &:after {
                        content: "Collapse";
                    }
                    ${accordionIconStyle}
                `,
            },
            headerProps: {
                css: css`
                    background: transparent;
                    padding-top: 16px;
                    border-top: 1px solid ${color("common.border")};
                    button,
                    button:focus,
                    ${AccordionSectionPanel} + & button:focus {
                        padding: 0;
                        border: 0;
                    }
                    ${xScreen("lte", "md")} {
                        button,
                        button > div {
                            align-items: baseline;
                            margin-bottom: 0;
                        }
                    }
                    ${xScreen("lte", "xs")} {
                        button a {
                            width: 100%;
                        }
                    }
                `,
            },
            panelProps: {
                css: css`
                    background: transparent;
                    padding: 0;
                    border: 0;
                `,
            },
        },
        bodyGridContainer: {
            gap: 0,
            css: css`
                margin-top: 18px;
            `,
        },
    },
    accrodionTitle: {
        wrapper: {
            css: css`
                margin-bottom: 0;
                ${xScreen("lte", "md")} {
                    flex-direction: row;
                    align-items: center;
                }
            `,
        },
    },
};

const SectionWrapper: FC<Props> = ({
    extendedStyles,
    children,
    link,
    onLinkClick,
    linkText,
    openInNewTab,
    title,
    isAccordion,
    className,
    extraChild,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(sectionWrapperStyles, extendedStyles));

    const titleTypograhy = title ? (
        <TitleTypography
            link={link}
            onLinkClick={onLinkClick}
            linkText={linkText}
            openInNewTab={openInNewTab}
            extendedStyles={isAccordion ? styles.accrodionTitle : styles.title}
            extraChild={extraChild}
        >
            {title}
        </TitleTypography>
    ) : null;

    if (isAccordion) {
        return (
            <StyledWrapper className={`accordion-section-wrapper ${className || ""}`}>
                <Accordion headingLevel={2}>
                    <ManagedAccordionSection
                        title={titleTypograhy}
                        initialExpanded={true}
                        {...styles.accordionStyles?.managerAccordionSection}
                    >
                        <GridContainer {...styles.accordionStyles?.bodyGridContainer}>{children}</GridContainer>
                    </ManagedAccordionSection>
                </Accordion>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper className={`section-wrapper ${className || ""}`} {...styles.wrapper} {...otherProps}>
            {titleTypograhy}
            <StyledWrapper>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default SectionWrapper;
